import { useEffect, useMemo, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { Spinner } from "@/components/ui";
import { useBuildersList } from "./useBuildersList";
import { BuilderFormDrawer } from "./BuilderFormDrawer";
import "../clients/clients.css";
import "./builders.css";

/** The builder directory (Builders Build Brief 01 §7) — one GET /builders/list
 *  drives a searchable table; each name links to the builder hub at
 *  /staff/builders/:id. Search is client-side over the in-memory set; the fetch
 *  lives in useBuildersList (SWR cache + dedupe, same as clients). "New
 *  builder" in the header routes here with { create: true }. */
export function BuildersList() {
  const { rows, total, loading, refreshing, error, reload } = useBuildersList();
  const [qInput, setQInput] = useState("");
  const [q, setQ] = useState("");
  const [includeArchived, setIncludeArchived] = useState(false);
  const [showNew, setShowNew] = useState(false);
  const [dismissedError, setDismissedError] = useState<string | undefined>();
  const visibleError = error && error !== dismissedError ? error : undefined;

  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    if ((location.state as { create?: boolean } | null)?.create) {
      setShowNew(true);
      navigate(location.pathname, { replace: true, state: null });
    }
  }, [location, navigate]);

  useEffect(() => {
    const t = setTimeout(() => setQ(qInput.trim()), 300);
    return () => clearTimeout(t);
  }, [qInput]);

  const filtered = useMemo(() => {
    const needle = q.toLowerCase();
    return rows.filter((r) => {
      if (!includeArchived && r.is_archived) return false;
      if (needle) {
        const haystack = `${r.company_name} ${r.contact_name} ${r.email} ${r.phone} ${r.trade}`.toLowerCase();
        if (!haystack.includes(needle)) return false;
      }
      return true;
    });
  }, [rows, q, includeArchived]);

  return (
    <section className="clients-list">
      <header className="clients-list__head">
        <div>
          <h1>Builders</h1>
          <p className="clients-list__count">
            {total} builder{total === 1 ? "" : "s"}
            {filtered.length !== total ? <span className="clients-list__sub"> · {filtered.length} shown</span> : null}
            {refreshing ? <span className="jobs-list__refreshing"> · Refreshing…</span> : null}
          </p>
        </div>
      </header>

      <div className="clients-list__filters">
        <input
          className="leads__search clients-list__search"
          type="search"
          placeholder="Search firm, contact, email, trade…"
          value={qInput}
          onChange={(e) => setQInput(e.target.value)}
        />
        <label className="clients-list__toggle">
          <input type="checkbox" checked={includeArchived} onChange={(e) => setIncludeArchived(e.target.checked)} />
          Include archived
        </label>
      </div>

      {visibleError ? (
        <p className="leads__error" role="alert" onClick={() => setDismissedError(error)}>{visibleError}</p>
      ) : null}

      {loading ? (
        <div className="clients-list__loading"><Spinner label="Loading builders…" /></div>
      ) : filtered.length === 0 ? (
        <p className="clients-list__empty">
          {total === 0 ? "No builders yet. Add the firms you work with, then attach them to jobs to bring them into the chat." : "No builders match."}
        </p>
      ) : (
        <div className="jobs-table" role="table" aria-label="Builders">
          <div className="jobs-table__head builders-table__cols" role="row">
            <span role="columnheader">Firm</span>
            <span role="columnheader">Contact</span>
            <span role="columnheader">Email</span>
            <span role="columnheader">Phone</span>
            <span role="columnheader">Trade</span>
            <span role="columnheader">Jobs</span>
            <span role="columnheader">Logins</span>
          </div>
          {filtered.map((row) => (
            <div key={row.id} className="jobs-table__row builders-table__cols clients-row" role="row">
              <span role="cell" data-label="Firm" className="clients-table__name">
                <Link to={`/staff/builders/${row.id}`} className="clients-table__display clients-row__link">
                  {row.company_name}
                </Link>
                <span className="clients-table__badges">
                  {row.is_archived ? <span className="clients-badge clients-badge--muted">Archived</span> : null}
                </span>
              </span>
              <span role="cell" data-label="Contact">{row.contact_name || "—"}</span>
              <span role="cell" data-label="Email" className="clients-table__email">
                {row.email ? <a href={`mailto:${row.email}`} className="clients-row__action">{row.email}</a> : "—"}
              </span>
              <span role="cell" data-label="Phone">
                {row.phone ? <a href={`tel:${row.phone}`} className="clients-row__action">{row.phone}</a> : "—"}
              </span>
              <span role="cell" data-label="Trade">{row.trade || "—"}</span>
              <span role="cell" data-label="Jobs" className="numeric">{row.job_count}</span>
              <span role="cell" data-label="Logins" className="numeric">{row.people_count}</span>
            </div>
          ))}
        </div>
      )}

      {showNew ? (
        <BuilderFormDrawer
          onClose={() => setShowNew(false)}
          onSaved={() => {
            setShowNew(false);
            reload();
          }}
        />
      ) : null}
    </section>
  );
}
